import { useState } from 'react';
import { CardWizard } from '../../../layouts';
import { HeadingWizard } from '../..';
import { useNotification } from '../../../contexts';
import { useExecuteQueryMutation } from '../../../store/rtkQuery/utility';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Input, Space, Alert, Typography } from 'antd';
import { HistoryOutlined, ArrowLeftOutlined, PlayCircleOutlined } from '@ant-design/icons';
import ExecutionLogsDrawer from './ExecutionLogsDrawer';

const { TextArea } = Input;
const { Text } = Typography;

export default function ExecuteQuery() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [query, setQuery] = useState('');
  const [executeQuery, { isLoading }] = useExecuteQueryMutation();
  const [result, setResult] = useState<{ message: string; data?: any[] } | null>(null);
  const [isLogsDrawerOpen, setIsLogsDrawerOpen] = useState(false);

  const handleExecute = async () => {
    if (!query.trim()) {
      return showNotification('error', 'Error', 'Query cannot be empty');
    }

    const lowerQuery = query.trim().toLowerCase();
    const sqlKeywords = ['select', 'insert', 'update', 'delete', 'with', 'create', 'alter', 'drop', 'truncate', 'grant', 'revoke'];
    
    const isValidSql = sqlKeywords.some(keyword => lowerQuery.startsWith(keyword));
    if (!isValidSql) {
      return showNotification('error', 'Error', 'Please enter a valid PostgreSQL query');
    }

    try {
      const response = await executeQuery({ query: query.trim() }).unwrap();
      setResult({
        message: response.message || 'Query executed successfully',
        data: response.data || [],
      });
      showNotification('success', 'Success', response.message || 'Query executed successfully');
    } catch (error: any) {
      console.error(error);
      const errorMsg = error?.data?.message || error?.message || 'Failed to execute query';
      showNotification('error', 'Error', errorMsg);
      setResult(null);
    }
  };

  const renderResult = () => {
    if (!result) return null;

    if (result.data && Array.isArray(result.data) && result.data.length > 0) {
      const firstRow = result.data[0];
      const columns = Object.keys(firstRow).map(key => ({
        title: key,
        dataIndex: key,
        key: key,
      }));

      return (
        <div className="mt-6 w-full overflow-x-auto bg-white p-4 rounded-md border border-gray-100 shadow-sm">
          <Alert message={result.message} type="success" showIcon className="mb-4" />
          <Table
            dataSource={result.data}
            columns={columns}
            rowKey={(record, index) => index?.toString() || Math.random().toString()}
            pagination={{ pageSize: 10 }}
            size="small"
            scroll={{ x: 'max-content' }}
            bordered
          />
        </div>
      );
    }

    return (
      <div className="mt-6 w-full">
        <Alert
          message="Execution Successful"
          description={
            <div>
              <Text strong className="block mb-1">{result.message}</Text>
              {result.data && Array.isArray(result.data) && result.data.length === 0 && (
                <Text type="secondary" className="text-xs">No rows returned.</Text>
              )}
            </div>
          }
          type="success"
          showIcon
        />
      </div>
    );
  };

  return (
    <div className="p-3 flex flex-col gap-4">
      <CardWizard addOnClass="p-6 min-h-[500px]">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
          <HeadingWizard addOnClass="text-[#6E6893] font-extrabold uppercase tracking-wider text-sm flex items-center gap-x-2">
            <span>Execute SQL Query</span>
          </HeadingWizard>
          <Space>
            <Button
              type="primary"
              ghost
              icon={<HistoryOutlined />}
              onClick={() => setIsLogsDrawerOpen(true)}
              size="middle"
            >
              View Logs
            </Button>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              size="middle"
            >
              Back
            </Button>
          </Space>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Text strong className="text-gray-700">SQL Query (PostgreSQL)</Text>
            <TextArea
              rows={8}
              className="font-mono text-sm"
              placeholder="e.g., SELECT * FROM public.users LIMIT 10;"
              value={query}
              onChange={e => setQuery(e.target.value)}
              style={{ backgroundColor: '#fafafa', borderRadius: '8px' }}
            />
          </div>

          <div className="flex justify-end mt-2">
            <Button
              type="primary"
              size="large"
              icon={<PlayCircleOutlined />}
              onClick={handleExecute}
              disabled={isLoading || !query.trim()}
              loading={isLoading}
              style={{ backgroundColor: '#4649EC', borderColor: '#4649EC', padding: '0 32px' }}
            >
              Execute Query
            </Button>
          </div>

          {renderResult()}
        </div>
      </CardWizard>
      <ExecutionLogsDrawer open={isLogsDrawerOpen} onClose={() => setIsLogsDrawerOpen(false)} />
    </div>
  );
}
